import { makeObservable, observable, computed, action } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import type { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import type { ISyncOperation } from "@/store/sync/operations/types";
import { STEP_CONFIGS, StepId, type Step, type StepConfig } from "./step-configs";
import { logger } from "@/modules/logger";
import { trackEvent, TrackedEvent } from "@/domains/metrics";

export interface GettingStartedProgressData {
  steps: Array<{
    id: StepId;
    completedAt?: string;
  }>;
  isDismissed: boolean;
  isCollapsed: boolean;
  isOnboardingTooltipDismissed: boolean;
  hasShownConfetti: boolean;
  hasSeenGettingStartedCard: boolean;
}

export class AppStoreGettingStartedStore extends AppSubStore {
  steps: Step[] = [];
  isDismissed = false;
  isCollapsed = false;
  isOnboardingTooltipDismissed = false;
  hasShownConfetti = false;
  hasSeenGettingStartedCard = false;
  private hasTrackedAllComplete = false;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.initializeSteps();

    makeObservable<
      this,
      "initializeSteps" | "persist" | "hasTrackedAllComplete" | "checkAndTrackAllComplete"
    >(this, {
      steps: observable,
      isDismissed: observable,
      isCollapsed: observable,
      isOnboardingTooltipDismissed: observable,
      hasShownConfetti: observable,
      hasSeenGettingStartedCard: observable,
      handleOperation: action,
      markStepComplete: action,
      dismissGuide: action,
      dismissOnboardingTooltip: action,
      toggleGuide: action,
      resetProgress: action,
      markConfettiShown: action,
      markGettingStartedCardSeen: action,
      isGuideVisible: computed,
      isOnboardingTooltipVisible: computed,
      isCollapsedSidebarTooltipVisible: computed,
      progress: computed,
      isComplete: computed,
      completedCount: computed,
      totalCount: computed,
      restore: action,
      load: action,
      initializeSteps: false,
      getStep: false,
      getStepConfig: false,
      toJSON: false,
      persist: false,
      hasTrackedAllComplete: false,
      checkAndTrackAllComplete: false,
    });
  }

  /**
   * Initialize all guide steps from configuration
   */
  private initializeSteps(): void {
    this.steps = STEP_CONFIGS.map(config => ({
      id: config.id,
      title: config.title,
      completedAt: undefined,
    }));
  }

  /**
   * Check and track if all steps are now complete
   */
  private checkAndTrackAllComplete(): void {
    if (this.isComplete && !this.hasTrackedAllComplete) {
      this.hasTrackedAllComplete = true;
      logger.info({
        message: "[GettingStarted] All steps complete - tracking event",
      });
      trackEvent(TrackedEvent.GettingStartedAllComplete, {});
    }
  }

  /**
   * Main detection handler - called from BaseSyncOperation
   */
  handleOperation(operation: BaseSyncOperation<ISyncOperation>): void {
    for (const stepConfig of STEP_CONFIGS) {
      // Only check steps that have operation matchers
      if (!stepConfig.matchesOperation) {
        continue;
      }

      const step = this.steps.find(s => s.id === stepConfig.id);
      if (!step || step.completedAt) {
        continue;
      }

      if (stepConfig.matchesOperation(operation)) {
        step.completedAt = new Date();
        logger.debug({
          message: `[GettingStarted] Step completed: ${step.id}`,
          info: { stepId: step.id, stepTitle: step.title },
        });

        // Track step completion
        trackEvent(TrackedEvent.GettingStartedStepCompleted, {
          step_id: step.id,
          step_title: step.title,
          completed_count: this.completedCount,
          total_count: this.totalCount,
        });

        // Check if all steps are now complete
        this.checkAndTrackAllComplete();

        this.persist();
      }
    }
  }

  /**
   * Mark step complete by ID (for non-operation detections)
   */
  markStepComplete(stepId: StepId): void {
    const step = this.steps.find(s => s.id === stepId);
    if (!step || step.completedAt) {
      return;
    }

    step.completedAt = new Date();
    logger.debug({
      message: `[GettingStarted] Step completed: ${stepId}`,
      info: { stepId, stepTitle: step.title },
    });

    // Track step completion
    trackEvent(TrackedEvent.GettingStartedStepCompleted, {
      step_id: stepId,
      step_title: step.title,
      completed_count: this.completedCount,
      total_count: this.totalCount,
    });

    // Check if all steps are now complete
    this.checkAndTrackAllComplete();

    this.persist();
  }

  /**
   * Get step by ID
   */
  getStep(stepId: StepId): Step | undefined {
    return this.steps.find(s => s.id === stepId);
  }

  /**
   * Get step configuration by ID
   */
  getStepConfig(stepId: StepId): StepConfig | undefined {
    return STEP_CONFIGS.find(config => config.id === stepId);
  }

  get isGuideVisible(): boolean {
    // The getting-started guide is superseded by the floating agent onboarding.
    return false;
  }

  /**
   * Computed visibility for onboarding tooltip
   * Shows when guide is visible, no steps are completed, and tooltip hasn't been dismissed
   */
  get isOnboardingTooltipVisible(): boolean {
    // Don't show if tooltip was already dismissed
    if (this.isOnboardingTooltipDismissed) {
      return false;
    }

    // Don't show if any step is completed
    if (this.completedCount > 0) {
      return false;
    }

    // Show if guide is visible
    return this.isGuideVisible;
  }

  /**
   * Computed visibility for "New to Mem?" tooltip in collapsed sidebar
   * Shows when guide is visible, no steps are completed, tooltip hasn't been dismissed,
   * and user hasn't seen the getting started card yet
   */
  get isCollapsedSidebarTooltipVisible(): boolean {
    // Don't show if user has already seen the getting started card in expanded sidebar
    if (this.hasSeenGettingStartedCard) {
      return false;
    }

    // Use the same logic as the regular onboarding tooltip
    return this.isOnboardingTooltipVisible;
  }

  /**
   * Dismiss the guide permanently
   */
  dismissGuide = (): void => {
    this.isDismissed = true;

    // Track dismissal
    trackEvent(TrackedEvent.GettingStartedDismissed, {});

    this.persist();
  };

  /**
   * Dismiss the onboarding tooltip permanently
   */
  dismissOnboardingTooltip = (): void => {
    this.isOnboardingTooltipDismissed = true;

    // Track dismissal
    trackEvent(TrackedEvent.GettingStartedOnboardingTooltipDismissed, {});

    this.persist();
  };

  /**
   * Toggle the collapsed/expanded state
   */
  toggleGuide = (isExpanded: boolean): void => {
    this.isCollapsed = !isExpanded;

    // Track collapse/expand
    trackEvent(TrackedEvent.GettingStartedToggled, {
      is_expanded: !this.isCollapsed,
    });

    this.persist();
  };

  /**
   * Mark confetti as shown
   */
  markConfettiShown = (): void => {
    this.hasShownConfetti = true;
    this.persist();
  };

  /**
   * Mark that user has seen the getting started card
   */
  markGettingStartedCardSeen = (): void => {
    if (this.hasSeenGettingStartedCard) {
      return;
    }
    this.hasSeenGettingStartedCard = true;
    this.persist();
  };

  /**
   * Reset all progress
   */
  resetProgress(): void {
    for (const step of this.steps) {
      step.completedAt = undefined;
    }
    this.isDismissed = false;
    this.isCollapsed = false;
    this.isOnboardingTooltipDismissed = false;
    this.hasShownConfetti = false;
    this.hasSeenGettingStartedCard = false;
    this.hasTrackedAllComplete = false;
    this.persist();
  }

  /**
   * Count of completed steps
   */
  get completedCount(): number {
    return this.steps.filter(s => s.completedAt).length;
  }

  /**
   * Total number of steps
   */
  get totalCount(): number {
    return this.steps.length;
  }

  /**
   * Overall progress percentage (0-1)
   */
  get progress(): number {
    return this.totalCount > 0 ? this.completedCount / this.totalCount : 0;
  }

  /**
   * Is guide fully complete
   */
  get isComplete(): boolean {
    return this.completedCount === this.totalCount && this.totalCount > 0;
  }

  /**
   * Serialize to JSON for persistence
   */
  toJSON() {
    return {
      steps: this.steps.map(step => ({
        id: step.id,
        completedAt: step.completedAt?.toISOString(),
      })),
      isDismissed: this.isDismissed,
      isCollapsed: this.isCollapsed,
      isOnboardingTooltipDismissed: this.isOnboardingTooltipDismissed,
      hasShownConfetti: this.hasShownConfetti,
      hasSeenGettingStartedCard: this.hasSeenGettingStartedCard,
    };
  }

  /**
   * Restore from persisted data
   */
  restore(data: GettingStartedProgressData): void {
    this.isDismissed = data.isDismissed ?? false;
    this.isCollapsed = data.isCollapsed ?? false;
    this.isOnboardingTooltipDismissed = data.isOnboardingTooltipDismissed ?? false;
    this.hasShownConfetti = data.hasShownConfetti ?? false;
    this.hasSeenGettingStartedCard = data.hasSeenGettingStartedCard ?? false;

    if (data.steps && Array.isArray(data.steps)) {
      for (const stepData of data.steps) {
        const step = this.steps.find(s => s.id === stepData.id);
        if (step && stepData.completedAt) {
          step.completedAt = new Date(stepData.completedAt);
        }
      }
    }
  }

  /**
   * Persist to IndexedDB
   */
  private async persist(): Promise<void> {
    try {
      const data = this.toJSON();
      await this.store.memDb.settings.setGettingStartedProgress(data);
    } catch (err) {
      logger.error({
        message: "[GettingStarted] Failed to persist progress",
        info: { error: String(err) },
      });
    }
  }

  /**
   * Load from IndexedDB
   */
  async load(): Promise<void> {
    try {
      const data = await this.store.memDb.settings.getGettingStartedProgress();
      if (data) {
        this.restore(data as GettingStartedProgressData);
      }
    } catch (err) {
      logger.error({
        message: "[GettingStarted] Failed to load progress",
        info: { error: String(err) },
      });
    }
  }
}
